import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import TopBar from "@/components/TopBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Users, Copy, Trash2, Link as LinkIcon, ShieldCheck, Info } from "lucide-react";
import { api } from "@/lib/api";
import { swallow } from "@/lib/log";
import { toast } from "sonner";
import EmptyState from "@/components/EmptyState";

const CATEGORIES = [
  { key: "notes", label: "Notes" },
  { key: "documents", label: "Documents" },
  { key: "photos", label: "Photos" },
  { key: "passwords", label: "Passwords" },
  { key: "reminders", label: "Reminders" },
];

export default function Legacy() {
  const [settings, setSettings] = useState(null);
  const [nominees, setNominees] = useState([]);
  const [claims, setClaims] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", relation: "", allowed_categories: ["notes", "documents", "photos"] });

  const loadAll = async () => {
    // Fetch independently so a failure on any one endpoint (e.g. /claims)
    // doesn't erase the entire page. Each error is logged via swallow so we
    // still get a debug signal in the console.
    const results = await Promise.allSettled([
      api.get("/legacy/settings"),
      api.get("/legacy/nominees"),
      api.get("/legacy/claims"),
    ]);
    const [s, n, c] = results;
    if (s.status === "fulfilled") setSettings(s.value.data);
    else swallow("Legacy/settings")(s.reason);
    if (n.status === "fulfilled") setNominees(n.value.data);
    else swallow("Legacy/nominees")(n.reason);
    if (c.status === "fulfilled") setClaims(c.value.data);
    else swallow("Legacy/claims")(c.reason);
  };
  useEffect(() => { loadAll(); }, []);

  const addNominee = async () => {
    const email = (form.email || "").trim();
    const name = (form.name || "").trim();
    if (!email || !name) { toast.error("Name and email are required"); return; }
    // Basic email sanity — backend re-validates but this saves a round-trip
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Enter a valid email address");
      return;
    }
    if (!Array.isArray(form.allowed_categories) || form.allowed_categories.length === 0) {
      toast.error("Pick at least one category they can access");
      return;
    }
    try {
      const { data } = await api.post("/legacy/nominees", {
        email, name, relation: (form.relation || "").trim() || null,
        allowed_categories: form.allowed_categories,
      });
      setOpen(false);
      setForm({ email: "", name: "", relation: "", allowed_categories: ["notes", "documents", "photos"] });
      // Optimistic prepend so the user sees it immediately even if reload lags
      setNominees((prev) => [data, ...(prev || [])]);
      loadAll();
      toast.success(`${data.name} added — invite link copied?`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't create nominee");
    }
  };

  const revoke = async (nid) => {
    if (!window.confirm("Revoke this nominee?")) return;
    try { await api.delete(`/legacy/nominees/${nid}`); loadAll(); toast.success("Revoked"); }
    catch { toast.error("Failed"); }
  };

  const copy = async (val) => {
    try { await navigator.clipboard.writeText(val); toast.success("Copied invite link"); } catch { toast.error("Copy failed"); }
  };

  const saveSettings = async (patch) => {
    try { const { data } = await api.put("/legacy/settings", { ...settings, ...patch }); setSettings(data); toast.success("Saved"); }
    catch { toast.error("Failed"); }
  };

  const toggleCat = (k) => {
    setForm((f) => ({ ...f, allowed_categories: f.allowed_categories.includes(k)
      ? f.allowed_categories.filter((x) => x !== k)
      : [...f.allowed_categories, k] }));
  };

  return (
    <>
      <TopBar title="Digital Legacy" subtitle="Trusted people can access your vault later" back
        right={
          <button onClick={() => setOpen(true)} className="h-9 px-3 rounded-full bg-primary text-primary-foreground text-xs font-semibold" data-testid="legacy-add-nominee">
            + Nominee
          </button>
        }
      />
      <div className="px-4 py-4 space-y-4">
        <div className="rounded-2xl border border-border bg-card p-4" data-testid="legacy-settings-card">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-primary" />
            <div className="font-display font-semibold text-sm">Release rules</div>
          </div>
          <p className="text-xs text-muted-foreground mt-1">Access is granted when approvers agree AND either the immediate switch is on, or you've been inactive for N days.</p>
          {settings && (
            <div className="mt-3 space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Approvers needed</Label>
                  <Input type="number" min={1} value={settings.min_approvers} onChange={(e) => setSettings({ ...settings, min_approvers: Math.max(1, parseInt(e.target.value || "1", 10)) })} onBlur={() => saveSettings({ min_approvers: settings.min_approvers })} data-testid="legacy-min-approvers" />
                </div>
                <div>
                  <Label className="text-xs">Inactivity (days)</Label>
                  <Input type="number" min={0} value={settings.inactivity_days} onChange={(e) => setSettings({ ...settings, inactivity_days: Math.max(0, parseInt(e.target.value || "0", 10)) })} onBlur={() => saveSettings({ inactivity_days: settings.inactivity_days })} data-testid="legacy-inactivity-days" />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="legacy-immediate-toggle">
                <input type="checkbox" checked={!!settings.immediate_release} onChange={(e) => { setSettings({ ...settings, immediate_release: e.target.checked }); saveSettings({ immediate_release: e.target.checked }); }} />
                <span>Allow immediate release (skip inactivity wait)</span>
              </label>
              <div className="text-[11px] text-muted-foreground flex items-start gap-1">
                <Info className="h-3 w-3 mt-0.5 shrink-0" />
                <span>Last active: {settings.last_active_at ? new Date(settings.last_active_at).toLocaleString() : "—"}</span>
              </div>
            </div>
          )}
        </div>

        <div>
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Nominees</div>
          {nominees.length === 0 ? (
            <EmptyState icon={Users} title="No nominees yet" subtitle="Add trusted family or friends who can access your vault later." />
          ) : (
            <div className="space-y-2">
              {nominees.map((n) => (
                <div key={n.id} className="rounded-2xl border border-border bg-card p-4" data-testid="nominee-item">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <div className="font-display font-semibold text-sm truncate">{n.name}</div>
                        <Badge className="rounded-full text-[10px] capitalize">{n.status}</Badge>
                      </div>
                      <div className="text-xs text-muted-foreground truncate">{n.email}{n.relation ? ` • ${n.relation}` : ""}</div>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {n.allowed_categories.map((c) => <Badge key={c} variant="outline" className="rounded-full text-[10px]">{c}</Badge>)}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1">
                      <button onClick={() => copy(n.invite_url)} className="h-8 w-8 rounded-full hover:bg-muted flex items-center justify-center" title="Copy invite link" data-testid="nominee-copy-invite"><LinkIcon className="h-4 w-4" /></button>
                      {n.status !== "revoked" && (
                        <button onClick={() => revoke(n.id)} className="h-8 w-8 rounded-full hover:bg-muted text-muted-foreground hover:text-destructive flex items-center justify-center" data-testid="nominee-revoke"><Trash2 className="h-4 w-4" /></button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {claims.length > 0 && (
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Claims history</div>
            <div className="space-y-2">
              {claims.map((c) => (
                <div key={c.id} className="rounded-2xl border border-border bg-card p-3" data-testid="claim-item">
                  <div className="flex items-center gap-2">
                    <Badge className="rounded-full text-[10px] capitalize">{c.status}</Badge>
                    <span className="text-xs text-muted-foreground">{new Date(c.created_at).toLocaleString()}</span>
                  </div>
                  <div className="text-xs mt-1 text-muted-foreground">Approvals: {(c.approvals || []).length}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="font-display">Add nominee</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="nominee-form-name" /></div>
            <div><Label className="text-xs">Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="nominee-form-email" /></div>
            <div><Label className="text-xs">Relation</Label>
              <Input value={form.relation} onChange={(e) => setForm({ ...form, relation: e.target.value })} placeholder="e.g. spouse, sibling, friend" data-testid="nominee-form-relation" /></div>
            <div>
              <Label className="text-xs">Categories they can access</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {CATEGORIES.map((c) => (
                  <button type="button" key={c.key} onClick={() => toggleCat(c.key)}
                    className={`text-xs rounded-full px-3 py-1.5 border ${form.allowed_categories.includes(c.key) ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border text-muted-foreground"}`}
                    data-testid={`nominee-cat-${c.key}`}>{c.label}</button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={addNominee} data-testid="nominee-form-save">Create invite</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
